import { endOfDay } from "date-fns";

import type { ExperimentStatus } from "@hightouch/lib/experiments/types";

/**
 * Whether a test is over. A test ends only when `completed_at` is set and
 * already past — a *scheduled* end date, so a future one is still running.
 *
 * Promoting a winner is deliberately NOT ending: it stops sending the losing
 * variants (the dispatcher serves `promoted_variant_key`) while the test keeps
 * running and collecting metrics, so a promoted winner does not count here.
 *
 * `nowMs` defaults to the wall clock; tests pass a fixed time so the result is
 * deterministic in its arguments.
 */
export function hasTestEnded({
  completedAt,
  nowMs = Date.now(),
}: {
  completedAt: string | null;
  nowMs?: number;
}): boolean {
  return (
    completedAt != null &&
    new Date(completedAt).getTime() <= endOfDay(nowMs).getTime()
  );
}

/**
 * ND message A/B tests add one state to the shared experiment vocabulary:
 * `winner_promoted` — a winner has been promoted (only that arm sends) but the
 * measurement window is still open, so results keep collecting.
 */
export type AbTestStatus = ExperimentStatus | "winner_promoted";

type AbTestStatusInput = {
  /** `experiments.started_at` — stamped at the first send, not at setup. */
  startedAt: string | null;
  completedAt: string | null;
  /** `message_experiments.promoted_variant_key` — non-null once promoted. */
  promotedVariantKey: string | null;
  nowMs?: number;
};

/**
 * A message A/B test's state in the app's experiment vocabulary.
 *
 * Follows the `experiment_status` Postgres function that the campaigns list
 * reads off `experiments.status`, so a test can't read Running on its detail
 * page while the list calls it something else. Two deliberate differences:
 *
 * - **A test that hasn't run is Scheduled.**
 * - **It ends a test as soon as `hasTestEnded` says so with completedAt < NOW().**
 * - **A promoted winner reads `winner_promoted` until the window elapses.**
 *   Promotion sets `promoted_variant_key` and a future `completed_at`, so
 *   before that date the test is neither plain Running nor Completed — only the
 *   winner sends, but results keep collecting.
 *
 */
export function deriveAbTestStatus({
  startedAt,
  completedAt,
  promotedVariantKey,
  nowMs = Date.now(),
}: AbTestStatusInput): AbTestStatus {
  if (hasTestEnded({ completedAt, nowMs })) {
    return "completed";
  }
  if (promotedVariantKey != null) {
    return "winner_promoted";
  }
  if (startedAt == null || new Date(startedAt).getTime() > nowMs) {
    return "scheduled";
  }
  return "running";
}
